/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps, Theme } from '@mui/material';
import {
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ConfigType } from '@opengeoweb/shared';
import { uiActions, uiTypes } from '@opengeoweb/store';
import { ChevronDown, ChevronUp, ExitDomain } from '@opengeoweb/theme';
import type { WorkspaceModuleStore } from '@opengeoweb/workspace';
import {
  createBalancedMosaicNode,
  workspaceActions,
  selectors as workspaceSelectors,
} from '@opengeoweb/workspace';

const styles = {
  paper: {
    '& .MuiDrawer-paper': {
      top: '40px',
    },
  },
  drawer: {
    width: '250px',
    height: 'calc(100% -  40px)',
    overflowY: 'scroll',
  },
  nested: (theme: Theme): SxProps<Theme> => ({
    paddingLeft: theme.spacing(4),
  }),
  listItemPrimary: {
    '& .MuiListItemText-primary': {
      fontWeight: '500',
    },
  },
  listItemExternal: {
    paddingLeft: 0.5,
    color: 'geowebColors.typographyAndIcons.textLinkActive',
  },
};
export const PUBLIC_WARNINGS = 'Warnings';
export const TRAJKS = 'Trajks';

export const publicWarningDialogType = uiTypes.DialogTypes.PublicWarnings;

interface ConfigAppMenuLink {
  title: string;
  url: string;
}

const parseExternalApplications = (
  configExternalAppMenuLinks?: string,
): ConfigAppMenuLink[] => {
  if (!configExternalAppMenuLinks) {
    return [];
  }

  try {
    const parsedExternalAppMenuLinks = JSON.parse(
      // The CI/CD environment and config.json expect strings for its keys, therefore we need to
      // do a trick by encoding/decoding spaces and double quotes to end up with a valid json
      configExternalAppMenuLinks.replaceAll('%20', ' ').replaceAll("'", '"'),
    );
    if (Array.isArray(parsedExternalAppMenuLinks)) {
      return parsedExternalAppMenuLinks as ConfigAppMenuLink[];
    }
    return [];
  } catch (error) {
    console.error(
      'Failed to parse GW_EXTERNAL_APPLICATIONS_MENU_LINKS:',
      error,
    );
    return [];
  }
};

export interface MenuProps {
  config?: ConfigType;
}

const Menu: React.FC<MenuProps> = ({ config }: MenuProps) => {
  const dispatch = useDispatch();

  const mosaicNode = useSelector((store: WorkspaceModuleStore) =>
    workspaceSelectors.getMosaicNode(store),
  );

  const autoArrange = (): void => {
    dispatch(
      workspaceActions.updateWorkspaceViews({
        mosaicNode: createBalancedMosaicNode(mosaicNode),
      }),
    );
  };

  const openSyncgroupsDialog = React.useCallback(() => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.SyncGroups,
        setOpen: true,
      }),
    );
  }, [dispatch]);

  const fetchWorkspace = React.useCallback(
    (workspaceId: string) => {
      dispatch(workspaceActions.fetchWorkspace({ workspaceId }));
    },
    [dispatch],
  );

  const WARNINGS_WORKSPACE_ID =
    config?.GW_WARNINGS_WORKSPACE_ID ?? 'screenConfigWarnings';
  const SPACE_WEATHER_WORKSPACE_ID =
    config?.GW_SPACE_WEATHER_WORKSPACE_ID ?? 'spaceweather';
  const TRAJKS_WORKSPACE_ID = config?.GW_TRAJKS_WORKSPACE_ID;

  const showTaf = config?.GW_TAF_BASE_URL;
  const showNaf = config?.GW_FEATURE_NAF_ENABLED && showTaf;
  const showSigmet = config?.GW_HIDE_SIGMET_MENU
    ? false
    : config?.GW_SIGMET_BASE_URL;
  const showAirmet = config?.GW_HIDE_AIRMET_MENU
    ? false
    : config?.GW_AIRMET_BASE_URL;

  const [openAviation, setOpenAviation] = React.useState(true);

  const aviationProducts = [
    showAirmet && { id: 'screenConfigAirmet', title: 'AIRMET' },
    showSigmet && { id: 'screenConfigSigmet', title: 'SIGMET' },
    showTaf && { id: 'screenConfigTaf', title: 'TAF' },
    showNaf && { id: 'screenConfigNaf', title: 'NAF' },
  ].filter(Boolean) as { id: string; title: string }[];

  const showAviationMenu = aviationProducts.length > 0;

  const externalApplications = parseExternalApplications(
    config?.GW_EXTERNAL_APPLICATIONS_MENU_LINKS,
  );

  return (
    <List data-testid="drawer">
      {/* Auto arrange windows */}
      <ListItemButton
        onClick={(): void => {
          autoArrange();
        }}
      >
        <ListItemText
          primary="Auto Arrange Windows"
          sx={styles.listItemPrimary}
        />
      </ListItemButton>
      {/* Syn Groups */}
      <ListItemButton
        data-testid="syncGroupsButton"
        divider
        onClick={(): void => {
          openSyncgroupsDialog();
        }}
      >
        <ListItemText primary="Sync Groups" sx={styles.listItemPrimary} />
      </ListItemButton>

      {showAviationMenu ? (
        <>
          <ListItemButton
            onClick={(event: React.MouseEvent): void => {
              event.stopPropagation();
              setOpenAviation(!openAviation);
            }}
          >
            <ListItemText primary="Aviation" sx={styles.listItemPrimary} />
            {openAviation ? <ChevronUp /> : <ChevronDown />}
          </ListItemButton>
          <Collapse in={openAviation} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {aviationProducts.map((aviationProduct) => (
                <ListItemButton
                  dense
                  key={aviationProduct.id}
                  onClick={(): void => {
                    fetchWorkspace(aviationProduct.id);
                  }}
                  sx={styles.nested as SxProps<Theme>}
                >
                  <ListItemText primary={aviationProduct.title} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </>
      ) : null}

      {/* Space Weather */}
      {config?.GW_FEATURE_MODULE_SPACE_WEATHER ? (
        <ListItemButton
          key={SPACE_WEATHER_WORKSPACE_ID}
          onClick={(): void => {
            fetchWorkspace(SPACE_WEATHER_WORKSPACE_ID);
          }}
        >
          <ListItemText
            primary="Space Weather Forecast"
            sx={styles.listItemPrimary}
          />
        </ListItemButton>
      ) : null}

      {/** TRAJKS */}
      {TRAJKS_WORKSPACE_ID ? (
        <ListItemButton
          onClick={(): void => {
            fetchWorkspace(TRAJKS_WORKSPACE_ID);
          }}
        >
          <ListItemText primary={TRAJKS} sx={styles.listItemPrimary} />
        </ListItemButton>
      ) : null}

      {/** Warnings */}
      {config?.GW_DRAWINGS_BASE_URL !== undefined &&
        config?.GW_PRESET_BACKEND_URL !== undefined && (
          <ListItemButton
            onClick={(): void => {
              fetchWorkspace(WARNINGS_WORKSPACE_ID);
            }}
          >
            <ListItemText
              primary={PUBLIC_WARNINGS}
              sx={styles.listItemPrimary}
            />
          </ListItemButton>
        )}

      {/* External Applications */}
      {externalApplications.length > 0 && (
        <>
          <Divider component="li" />
          {externalApplications.map((configAppMenuLink) => (
            <ListItemButton
              component="a"
              href={configAppMenuLink.url}
              key={configAppMenuLink.title}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ExitDomain sx={styles.listItemExternal} />
              <ListItemText
                primary={configAppMenuLink.title}
                sx={styles.listItemExternal}
              />
            </ListItemButton>
          ))}
        </>
      )}
    </List>
  );
};

export default Menu;
